import { create } from 'zustand';
import { Shooting, ShootingEquipmentItem } from '../types';
import api from '../utils/api';
import { handleApiError, handleApiSuccess } from '../utils/errorHandler';
import { logger } from '../utils/logger';

// Persistance localStorage pour re-hydratation instantanée au boot
const SH_LS_KEY = 'nudge-shooting-store-v1';
const SH_LS_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
function loadShootingsLS(): Shooting[] {
  try {
    const raw = localStorage.getItem(SH_LS_KEY);
    if (!raw) return [];
    const data = JSON.parse(raw) as { shootings: Shooting[]; savedAt: number };
    if (!data || Date.now() - (data.savedAt || 0) > SH_LS_MAX_AGE_MS) return [];
    return Array.isArray(data.shootings) ? data.shootings : [];
  } catch { return []; }
}
// Cache stale-while-revalidate par contrat (évite un refetch à chaque switch). Invalidé sur mutation
// ET par le temps réel (socket) → un event force la fraîcheur, un simple switch sert le cache.
const _shCache = new Map<string, { data: Shooting[]; at: number }>();
const SH_TTL = 30_000;
export const invalidateShootingsCache = () => _shCache.clear();

/**
 * Fusionne un lot de shootings dans le tableau global SANS perdre les autres clients.
 * - fetch GLOBAL (pas de contractId) → le lot EST la vérité complète : on remplace tout.
 * - fetch SCOPÉ (contractId) → on remplace UNIQUEMENT les shootings de ce contrat et on
 *   conserve ceux des autres contrats. Sinon un refetch scopé (switch client, event socket)
 *   écrasait toute la liste avec un seul client → "tous les shootings disparaissent sauf 1".
 */
function mergeShootings(current: Shooting[], incoming: Shooting[], contractId?: string): Shooting[] {
  if (!contractId) return incoming; // source de vérité globale
  const others = current.filter(s => s.contractId !== contractId);
  return [...others, ...incoming];
}

let __shTimer: ReturnType<typeof setTimeout> | null = null;
function persistShootings(shootings: Shooting[]): void {
  if (__shTimer) return;
  __shTimer = setTimeout(() => {
    __shTimer = null;
    try {
      localStorage.setItem(SH_LS_KEY, JSON.stringify({ shootings, savedAt: Date.now() }));
    } catch {}
  }, 1000);
}

interface ShootingStore {
  shootings: Shooting[];
  defaultEquipment: ShootingEquipmentItem[];
  isLoading: boolean;

  fetchShootings: (contractId?: string) => Promise<void>;
  fetchUpcomingShootings: () => Promise<void>;
  fetchDefaultEquipment: () => Promise<void>;
  createShooting: (data: Partial<Shooting> & { postIds?: string[] }) => Promise<Shooting>;
  updateShooting: (id: string, data: Partial<Shooting> & { postIds?: string[] }) => Promise<Shooting>;
  deleteShooting: (id: string) => Promise<void>;
  uploadInspiration: (shootingId: string, file: File) => Promise<any>;
  addToAgenda: (id: string) => Promise<{ eventLink?: string | null }>;
  removeFromAgenda: (id: string) => Promise<void>;
}

export const useShootingStore = create<ShootingStore>((set, get) => ({
  shootings: loadShootingsLS(),
  defaultEquipment: [],
  isLoading: false,

  fetchShootings: async (contractId?: string) => {
    // Sert le cache instantanément au switch ; ne refetch (en arrière-plan) que si périmé (>30s).
    const key = contractId || '__all__';
    const cached = _shCache.get(key);
    if (cached) {
      set(state => ({ shootings: mergeShootings(state.shootings, cached.data, contractId) }));
      if (Date.now() - cached.at < SH_TTL) return; // frais → aucun réseau
    }
    try {
      const params: any = {};
      if (contractId) params.contractId = contractId;

      const response = await api.get('/instagram/shootings', { params });
      _shCache.set(key, { data: response.data, at: Date.now() });
      set(state => ({ shootings: mergeShootings(state.shootings, response.data, contractId) }));
      logger.log('✅ Shootings chargés:', response.data.length, contractId ? `(client ${contractId})` : '(tous)');
    } catch (error: any) {
      if (!cached) handleApiError(error, 'le chargement des shootings');
    }
  },

  fetchUpcomingShootings: async () => {
    try {
      const response = await api.get('/instagram/shootings', { params: { upcoming: 'true' } });
      // Merge upcoming into existing, avoid duplicates
      set(state => {
        const existingIds = new Set(state.shootings.map(s => s.id));
        const newOnes = response.data.filter((s: Shooting) => !existingIds.has(s.id));
        return { shootings: [...state.shootings, ...newOnes] };
      });
    } catch (error: any) {
      handleApiError(error, 'le chargement des prochains shootings');
    }
  },

  fetchDefaultEquipment: async () => {
    try {
      const response = await api.get('/instagram/shootings/defaults/equipment');
      set({ defaultEquipment: response.data });
    } catch (error: any) {
      // Fallback local si la route échoue
      set({
        defaultEquipment: [
          { name: 'iPhone 17 Pro (x2)', checked: false },
          { name: 'Trépied / Perche télescopique iPhone', checked: false },
          { name: 'Sony ZV-E1', checked: false },
          { name: 'Objectif 24-65mm', checked: false },
          { name: 'Objectif 24-70mm', checked: false },
          { name: 'Trépied appareil photo', checked: false },
          { name: 'Lampes LED (x2)', checked: false },
          { name: 'Batteries', checked: false },
          { name: 'Micros', checked: false },
          { name: 'Spot', checked: false },
        ]
      });
    }
  },

  createShooting: async (data) => {
    set({ isLoading: true });
    try {
      const response = await api.post('/instagram/shootings', data);
      const shooting = response.data.shooting;
      _shCache.clear();

      set(state => ({
        shootings: [...state.shootings, shooting],
        isLoading: false
      }));

      handleApiSuccess('Shooting créé avec succès');
      logger.log('✅ Shooting créé:', shooting.id);
      return shooting;
    } catch (error: any) {
      handleApiError(error, 'la création du shooting');
      set({ isLoading: false });
      throw error;
    }
  },

  updateShooting: async (id, data) => {
    set({ isLoading: true });
    try {
      const response = await api.put(`/instagram/shootings/${id}`, data);
      const shooting = response.data.shooting;
      _shCache.clear();

      set(state => ({
        shootings: state.shootings.map(s => s.id === id ? shooting : s),
        isLoading: false
      }));

      handleApiSuccess('Shooting mis à jour');
      logger.log('✅ Shooting mis à jour:', shooting.id);
      return shooting;
    } catch (error: any) {
      handleApiError(error, 'la mise à jour du shooting');
      set({ isLoading: false });
      throw error;
    }
  },

  deleteShooting: async (id) => {
    set({ isLoading: true });
    try {
      await api.delete(`/instagram/shootings/${id}`);
      _shCache.clear();

      set(state => ({
        shootings: state.shootings.filter(s => s.id !== id),
        isLoading: false
      }));

      handleApiSuccess('Shooting supprimé');
      logger.log('✅ Shooting supprimé:', id);
    } catch (error: any) {
      handleApiError(error, 'la suppression du shooting');
      set({ isLoading: false });
      throw error;
    }
  },

  uploadInspiration: async (shootingId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/instagram/shootings/${shootingId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Recharger le shooting pour avoir les inspis à jour
      const shootingRes = await api.get(`/instagram/shootings/${shootingId}`);
      set(state => ({
        shootings: state.shootings.map(s => s.id === shootingId ? shootingRes.data : s)
      }));

      return response.data.inspiration;
    } catch (error: any) {
      handleApiError(error, "l'upload du fichier d'inspiration");
      throw error;
    }
  },

  addToAgenda: async (id) => {
    try {
      const response = await api.post(`/instagram/shootings/${id}/agenda`);
      const shooting = response.data.shooting;
      set(state => ({
        shootings: state.shootings.map(s => s.id === id ? shooting : s)
      }));
      handleApiSuccess('Ajouté à Google Agenda');
      return { eventLink: response.data.eventLink };
    } catch (error: any) {
      handleApiError(error, "l'ajout à l'agenda");
      throw error;
    }
  },

  removeFromAgenda: async (id) => {
    try {
      await api.delete(`/instagram/shootings/${id}/agenda`);
      set(state => ({
        shootings: state.shootings.map(s => s.id === id ? { ...s, googleEventId: null, agendaSyncedAt: null } : s)
      }));
      handleApiSuccess("Retiré de l'agenda");
    } catch (error: any) {
      handleApiError(error, "le retrait de l'agenda");
      throw error;
    }
  }
}));

// Persistance localStorage debouncée à chaque update de shootings
let __lastShootings: Shooting[] = useShootingStore.getState().shootings;
useShootingStore.subscribe((state) => {
  if (state.shootings !== __lastShootings) {
    __lastShootings = state.shootings;
    persistShootings(state.shootings);
  }
});
